import { useState, useRef, useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Clock, CheckCircle2, XCircle } from 'lucide-react';
import { useWebSocket } from '@/hooks/useWebSocket';
import { apiClient } from '@/api/client';
import { useMobileStore } from '@/stores/mobile-store';
import { SkeletonText } from '@/components/shared/Skeleton';
import type { LogLevel, QueryHistoryEntry } from '@/types';

// ═══════════════════════════════════════════
// Server raw types (snake_case from SQLite)
// ═══════════════════════════════════════════

interface RawHistoryRow {
  id: string;
  connection_id: string;
  sql: string;
  status: 'success' | 'error';
  duration_ms: number;
  row_count: number | null;
  error: string | null;
  executed_at: string;
}

function mapHistoryRow(raw: RawHistoryRow): QueryHistoryEntry {
  return {
    id: raw.id,
    connectionId: raw.connection_id,
    sql: raw.sql,
    status: raw.status,
    durationMs: raw.duration_ms,
    rowCount: raw.row_count,
    error: raw.error,
    executedAt: raw.executed_at,
  };
}

// ═══════════════════════════════════════════
// Helpers
// ═══════════════════════════════════════════

const LEVEL_COLORS: Record<LogLevel, string> = {
  info: 'text-accent-blue',
  warn: 'text-accent-yellow',
  error: 'text-accent-red',
  debug: 'text-text-muted',
  query: 'text-accent-purple',
};

const LEVEL_BG: Record<LogLevel, string> = {
  info: 'rgba(59, 130, 246, 0.1)',
  warn: 'rgba(234, 179, 8, 0.1)',
  error: 'rgba(239, 68, 68, 0.1)',
  debug: 'transparent',
  query: 'rgba(168, 85, 247, 0.1)',
};

function formatTime(ts: string): string {
  try {
    const d = new Date(ts);
    return d.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
  } catch {
    return ts;
  }
}

function formatTimestamp(ts: string): string {
  try {
    const d = new Date(ts);
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  } catch {
    return ts;
  }
}

function truncateSql(sql: string, maxLen = 60): string {
  const oneLine = sql.replace(/\s+/g, ' ').trim();
  if (oneLine.length <= maxLen) return oneLine;
  return oneLine.slice(0, maxLen) + '...';
}

// ═══════════════════════════════════════════
// Logs Screen
// ═══════════════════════════════════════════

type LogsTab = 'logs' | 'history';

export function LogsScreen() {
  const [activeTab, setActiveTab] = useState<LogsTab>('logs');

  return (
    <div className="h-full flex flex-col">
      {/* Tab switcher */}
      <div
        className="flex border-b shrink-0"
        style={{ borderColor: 'var(--border-default)' }}
      >
        <TabButton
          label="Logs"
          active={activeTab === 'logs'}
          onClick={() => setActiveTab('logs')}
        />
        <TabButton
          label="History"
          active={activeTab === 'history'}
          onClick={() => setActiveTab('history')}
        />
      </div>

      {/* Tab content */}
      <div className="flex-1 min-h-0">
        {activeTab === 'logs' ? <LogsTab /> : <HistoryTab />}
      </div>
    </div>
  );
}

function TabButton({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`flex-1 text-center text-sm font-medium py-2.5 border-b-2 transition-colors ${
        active
          ? 'text-accent-blue border-accent-blue'
          : 'text-text-muted border-transparent'
      }`}
    >
      {label}
    </button>
  );
}

// ═══════════════════════════════════════════
// Real-time Logs Tab
// ═══════════════════════════════════════════

function LogsTab() {
  const { logs } = useWebSocket();
  const containerRef = useRef<HTMLDivElement>(null);
  const isScrollLockedRef = useRef(true);

  // Auto-scroll to bottom
  useEffect(() => {
    const el = containerRef.current;
    if (el && isScrollLockedRef.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [logs]);

  const handleScroll = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 20;
    isScrollLockedRef.current = atBottom;
  }, []);

  return (
    <div
      ref={containerRef}
      className="h-full overflow-y-auto"
      onScroll={handleScroll}
    >
      {logs.length === 0 && (
        <div className="flex items-center justify-center h-full text-text-muted text-sm">
          No log entries yet
        </div>
      )}
      {logs.map((entry, i) => (
        <div
          key={`${entry.timestamp}-${i}`}
          className="px-3 py-2 border-b"
          style={{
            borderColor: 'var(--border-subtle)',
            backgroundColor: LEVEL_BG[entry.level],
          }}
        >
          <div className="flex items-center gap-2">
            <span className="text-xs text-text-muted font-mono">
              {formatTime(entry.timestamp)}
            </span>
            <span
              className={`text-[10px] uppercase font-semibold ${LEVEL_COLORS[entry.level]}`}
            >
              {entry.level}
            </span>
            {entry.durationMs != null && (
              <span className="text-[10px] text-accent-green ml-auto">
                {entry.durationMs}ms
              </span>
            )}
          </div>
          <div className="text-sm text-text-secondary mt-0.5 break-words">
            {entry.message}
          </div>
        </div>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════
// Query History Tab
// ═══════════════════════════════════════════

function HistoryTab() {
  const { data: history = [], isLoading } = useQuery<QueryHistoryEntry[]>({
    queryKey: ['queryHistory'],
    queryFn: async () => {
      const raw = await apiClient.get<RawHistoryRow[]>('/api/query/history');
      return raw.map(mapHistoryRow);
    },
    refetchInterval: 10_000,
  });

  const handleTapHistory = useCallback(
    (entry: QueryHistoryEntry) => {
      // Switch to query tab and open editor
      const store = useMobileStore.getState();
      store.setActiveTab('query');
      store.pushScreen({
        type: 'query-editor',
        title: 'Query Editor',
        params: { sql: entry.sql, connectionId: entry.connectionId },
      });
    },
    [],
  );

  return (
    <div className="h-full overflow-y-auto">
      {isLoading && (
        <div className="p-4">
          <SkeletonText lines={5} />
        </div>
      )}
      {!isLoading && history.length === 0 && (
        <div className="flex items-center justify-center h-full text-text-muted text-sm">
          No query history yet
        </div>
      )}
      {history.map((entry) => (
        <button
          key={entry.id}
          onClick={() => handleTapHistory(entry)}
          className="w-full text-left px-3 py-2.5 border-b active:bg-dbgo-tab-hover transition-colors"
          style={{ borderColor: 'var(--border-subtle)' }}
        >
          <div className="flex items-center gap-2">
            <div className="flex-shrink-0">
              {entry.status === 'success' ? (
                <CheckCircle2 size={14} className="text-accent-green" />
              ) : (
                <XCircle size={14} className="text-accent-red" />
              )}
            </div>
            <div className="text-xs text-text-secondary truncate font-mono flex-1 min-w-0">
              {truncateSql(entry.sql)}
            </div>
          </div>
          <div className="flex items-center gap-3 mt-1 ml-6">
            <span className="text-[10px] text-text-muted flex items-center gap-1">
              <Clock size={10} />
              {formatTimestamp(entry.executedAt)}
            </span>
            <span className="text-[10px] text-accent-green">
              {entry.durationMs}ms
            </span>
            {entry.rowCount != null && (
              <span className="text-[10px] text-text-muted">
                {entry.rowCount} row{entry.rowCount !== 1 ? 's' : ''}
              </span>
            )}
          </div>
          {entry.error && (
            <div className="text-[10px] text-accent-red mt-1 ml-6 truncate">
              {entry.error}
            </div>
          )}
        </button>
      ))}
    </div>
  );
}
